import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import { Edit, RefreshCw, MapPin, AlertTriangle } from "react-feather";
import { fetchBranches } from "../../utils/apiCalls";
import { coordinateStatus } from "../../utils/coordinateStatus";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Loader as RLoader } from "../reporting/ReportingShared";

const sameName = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

// The merchant system's names, refreshed on every branch sync, where they differ from ours.
// A branch not synced since the column existed has no merchant name and is never flagged.
const merchantNameDiff = (b) => ({
  name: b.merchant_name && !sameName(b.merchant_name, b.name) ? b.merchant_name : null,
  arabic: b.merchant_arabic_name && !sameName(b.merchant_arabic_name, b.arabicName) ? b.merchant_arabic_name : null,
});

const BranchesList = () => {
  const navigate = useNavigate();
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? merchantId : reduxSelectedMerchant;

  const [isLoading, setIsLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  // External Location ID is the Deliverect link. It's a long opaque id that cost a whole
  // column for something you only ever need to segment or look up, so it lives here as a
  // filter (and stays in the search haystack) instead of on every row.
  const [externalFilter, setExternalFilter] = useState("all");
  // A branch with an unusable pin is invisible to nearest-branch ranking in the
  // multi-merchant app. This filter is how you work through the backlog of them.
  const [pinFilter, setPinFilter] = useState("all");
  const [nameFilter, setNameFilter] = useState("all");

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      // includeBrands is opt-in server-side: the public pickup-app branch list omits the
      // brands relation, this page needs it.
      const response = await fetchBranches({ includeBrands: true }, false);
      setBranches(response?.data || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ---- Narrow by selected merchant (admin context) ----
  const merchantScoped =
    selectedMerchant && selectedMerchant !== "all"
      ? branches.filter((b) => b.MerchantId == selectedMerchant)
      : branches;

  // ---- KPIs ----
  const total = merchantScoped.length;
  const activeCount = merchantScoped.filter((b) => b.active).length;
  const inactiveCount = total - activeCount;
  const snoozedCount = merchantScoped.filter((b) => b.snooze_online_orders).length;

  // Computed once per row and reused by the tile, the filter and the badge, so all three
  // can never disagree about whether a branch has a usable pin.
  const pinOf = new Map(merchantScoped.map((b) => [b.id, coordinateStatus(b)]));
  // Counted over ACTIVE branches only: an inactive branch is not expected to be orderable,
  // so its missing pin is not a problem anyone needs to act on.
  const missingPinCount = merchantScoped.filter((b) => b.active && !pinOf.get(b.id).ok).length;

  // ---- Filters ----
  const filtered = merchantScoped.filter((b) => {
    if (statusFilter === "active" && !b.active) return false;
    if (statusFilter === "inactive" && b.active) return false;
    if (statusFilter === "snoozed" && !b.snooze_online_orders) return false;
    if (externalFilter === "linked" && !b.externalLocationId) return false;
    if (externalFilter === "unlinked" && b.externalLocationId) return false;
    if (pinFilter === "missing" && pinOf.get(b.id).ok) return false;
    if (pinFilter === "ok" && !pinOf.get(b.id).ok) return false;
    if (nameFilter === "differs") {
      const diff = merchantNameDiff(b);
      if (!diff.name && !diff.arabic) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      const brandNames = (b.brands || []).map((br) => br.name).join(" ");
      const hay = `${b.name || ""} ${b.arabicName || ""} ${b.merchant_name || ""} ${b.merchant_arabic_name || ""} ${b.merchant_branch_id || ""} ${b.externalLocationId || ""} ${b.brandReference || ""} ${b.address || ""} ${brandNames}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const hasBrandRef = filtered.some((b) => b.brandReference);
  // Only show the Brands column once at least one branch is tagged, matching how the
  // External Location / POS slug columns already appear only when relevant.
  const hasBrands = filtered.some((b) => (b.brands || []).length > 0);

  // ---- Shared pieces ----
  const StatusPill = ({ active }) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "4px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: active ? C.greenLight : C.coralLight,
        color: active ? C.green : C.coral,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: active ? C.green : C.coral }} />
      {active ? "Active" : "Inactive"}
    </span>
  );

  // Curbside / drive-thru, straight off the `fulfillment` object the branch endpoint
  // already returns. A mode with no config row comes back as null, which means the branch
  // simply doesn't offer it — that gets no chip at all, so a glance down the column shows
  // which branches do vehicular fulfillment.
  //
  // A configured mode shows as enabled (green), or Paused (orange) when staff have hit the
  // rush switch. Deliberately NOT surfacing the "closed" reason as its own state: that is a
  // time-of-day fact already carried by the Hours and Status columns, and a branch whose
  // weekly schedule is closed would otherwise show every mode greyed out — hiding the thing
  // this column exists to answer, which is whether the mode is set up at all. The live
  // reason stays available on hover.
  const MODE_LABELS = { curbside: "Curbside", drivethru: "Drive-Thru" };

  const FulfillmentPills = ({ fulfillment }) => {
    const modes = Object.keys(MODE_LABELS).filter((m) => fulfillment?.[m]);

    if (modes.length === 0) {
      return <span style={{ color: C.textLight, fontSize: 12 }}>—</span>;
    }

    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
        {modes.map((mode) => {
          const cfg = fulfillment[mode];
          const paused = cfg.reason === "paused";
          const tip = cfg.orderableNow
            ? `${MODE_LABELS[mode]}: available now`
            : `${MODE_LABELS[mode]}: not orderable right now (${cfg.reason})`;

          return (
            <span
              key={mode}
              title={tip}
              style={{
                display: "inline-block",
                padding: "3px 9px",
                borderRadius: 6,
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: 0.2,
                whiteSpace: "nowrap",
                background: paused ? C.orangeLight : C.greenLight,
                color: paused ? C.orange : C.green,
              }}
            >
              {MODE_LABELS[mode]}{paused ? " · Paused" : ""}
            </span>
          );
        })}
      </div>
    );
  };

  // Consumer brands attached to the branch. Inactive brands are shown muted rather than
  // hidden: the tag still exists, it just no longer participates in any app's filtering.
  const BrandChips = ({ brands }) => {
    if (!brands || brands.length === 0) {
      return <span style={{ color: C.textLight, fontSize: 12 }}>—</span>;
    }
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {brands.map((b) => (
          <span
            key={b.id}
            title={b.active === false ? `${b.name} (inactive)` : b.name}
            style={{
              display: "inline-block",
              padding: "3px 9px",
              borderRadius: 10,
              fontSize: 10.5,
              fontWeight: 700,
              whiteSpace: "nowrap",
              background: b.active === false ? C.bg : C.coralLight,
              color: b.active === false ? C.textLight : C.coralDark || C.coral,
              textDecoration: b.active === false ? "line-through" : "none",
            }}
          >
            {b.name}
          </span>
        ))}
      </div>
    );
  };

  // Why this branch cannot be ranked by distance, in a word, with the fix on hover. Nothing
  // is shown for a healthy pin — a column of green ticks would bury the handful of rows that
  // actually need attention.
  const PinWarning = ({ status }) => {
    if (status.ok) return null;
    const color = status.tone === "orange" ? C.orange : C.coral;
    const bg = status.tone === "orange" ? C.orangeLight : C.coralLight;
    return (
      <span
        title={`${status.hint}\n\nStored value: lat "${status.rawLat ?? ""}", lng "${status.rawLng ?? ""}"`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "4px 9px",
          borderRadius: 6,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.3,
          background: bg,
          color,
          cursor: "help",
        }}
      >
        <AlertTriangle size={11} /> {status.label}
      </span>
    );
  };

  const TagBadge = ({ label, color, bg }) => (
    <span
      style={{
        display: "inline-block",
        padding: "4px 10px",
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.3,
        background: bg,
        color,
      }}
    >
      {label}
    </span>
  );

  const ActionIcon = ({ icon: Icon, color, title, onClick }) => (
    <div
      onClick={onClick}
      title={title}
      style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.15s",
        border: `1px solid ${C.border}`,
        background: C.white,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = C.coralLight;
        e.currentTarget.style.borderColor = C.coral;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = C.white;
        e.currentTarget.style.borderColor = C.border;
      }}
    >
      <Icon size={15} color={color} />
    </div>
  );

  const filterSelectStyle = {
    padding: "7px 12px",
    fontSize: 12,
    borderRadius: 8,
    border: `1.5px solid ${C.border}`,
    fontFamily: "inherit",
    outline: "none",
    color: C.text,
    background: C.white,
    cursor: "pointer",
    minWidth: 140,
    transition: "border 0.15s",
  };
  const searchInputStyle = { ...filterSelectStyle, cursor: "text", minWidth: 240 };

  const handleEdit = (branch) => navigate(`/branches/edit-branch/${branch.id}`);

  return (
    <Fragment>
      <Breadcrumb title="Branches" parent="Dashboard" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          {/* KPI Tiles */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 16,
              marginBottom: 24,
            }}
          >
            <Tile label="Total Branches" value={total} icon="🏬" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
            <Tile label="Active" value={activeCount} icon="✅" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={60} />
            <Tile label="Inactive" value={inactiveCount} icon="⛔" gradient={`linear-gradient(135deg, #E74C3C, #FF6B6B)`} delay={120} />
            <Tile label="Snoozed" value={snoozedCount} icon="🌙" gradient={`linear-gradient(135deg, ${C.orange}, #FFD200)`} delay={180} />
            <Tile label="Missing Pin" value={missingPinCount} icon="📍" gradient={`linear-gradient(135deg, #8E44AD, #C39BD3)`} delay={240} />
          </div>

          {/* Only appears when there is something to act on. These branches cannot be
              returned as a nearest location, so a brand whose branches are all in this
              count is unreachable by distance in the multi-merchant app. */}
          {missingPinCount > 0 && (
            <div
              onClick={() => setPinFilter(pinFilter === "missing" ? "all" : "missing")}
              title="Show only these branches"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "12px 16px",
                marginBottom: 20,
                borderRadius: 10,
                cursor: "pointer",
                background: C.orangeLight,
                border: `1px solid ${C.orange}33`,
                color: C.text,
                fontSize: 13,
              }}
            >
              <AlertTriangle size={16} color={C.orange} />
              <span>
                <strong>{missingPinCount}</strong> active branch{missingPinCount === 1 ? " has" : "es have"}{" "}
                no usable map pin, so {missingPinCount === 1 ? "it cannot" : "they cannot"} be
                offered as a nearest location.{" "}
                <span style={{ color: C.orange, fontWeight: 700 }}>
                  {pinFilter === "missing" ? "Clear filter" : "Show them"}
                </span>
              </span>
            </div>
          )}

          {/* Filter bar + table */}
          <RCard>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 16,
                flexWrap: "wrap",
              }}
            >
              <div
                onClick={fetchData}
                title="Refresh"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  border: `1.5px solid ${C.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.15s",
                  background: C.white,
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = C.coral;
                  e.currentTarget.style.background = C.coralLight;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = C.border;
                  e.currentTarget.style.background = C.white;
                }}
              >
                <RefreshCw size={15} color={C.textMed} />
              </div>

              <input
                placeholder="Search name, reference, address..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={searchInputStyle}
              />

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={filterSelectStyle}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="snoozed">Snoozed</option>
              </select>

              {/* Replaces the old External Location ID column. The id itself is still
                  searchable and shown on the branch edit screen. */}
              <select
                value={externalFilter}
                onChange={(e) => setExternalFilter(e.target.value)}
                style={filterSelectStyle}
                title="Filter by whether the branch is linked to a Deliverect location"
              >
                <option value="all">All Locations</option>
                <option value="linked">Deliverect linked</option>
                <option value="unlinked">Not linked</option>
              </select>

              <select
                value={pinFilter}
                onChange={(e) => setPinFilter(e.target.value)}
                style={filterSelectStyle}
                title="Filter by whether the branch has usable map coordinates"
              >
                <option value="all">All Pins</option>
                <option value="missing">Missing / bad pin</option>
                <option value="ok">Has valid pin</option>
              </select>

              <select
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                style={filterSelectStyle}
                title="Branches whose name in the merchant system differs from ours (filled on Sync Branches)"
              >
                <option value="all">All Names</option>
                <option value="differs">Name differs from merchant</option>
              </select>

              <span
                style={{
                  marginLeft: "auto",
                  fontSize: 12,
                  color: C.textLight,
                  background: C.bg,
                  padding: "4px 12px",
                  borderRadius: 20,
                }}
              >
                {filtered.length} shown
              </span>
            </div>

            {isLoading ? (
              <RLoader />
            ) : filtered.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: C.textLight, fontSize: 13 }}>
                {branches.length === 0 ? "No branches yet." : "No branches match your filters."}
              </div>
            ) : (
              <div
                style={{
                  overflowX: "auto",
                  borderRadius: 10,
                  border: `1px solid ${C.border}`,
                }}
              >
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      <th style={tbl.th}>ID</th>
                      <th style={tbl.th}>Branch</th>
                      <th style={tbl.th}>Merchant Branch ID</th>
                      {/* Relabelled: this is the POS/Deliverect routing slug, unrelated to
                          the Brands column below. Two columns both called "Brand…" read as
                          the same thing and they are not. */}
                      {hasBrandRef && <th style={tbl.th}>POS Brand Slug</th>}
                      <th style={tbl.th}>Fulfillment</th>
                      {hasBrands && <th style={tbl.th}>Brands</th>}
                      <th style={tbl.th}>Hours</th>
                      <th style={tbl.th}>Status</th>
                      <th style={{ ...tbl.th, textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((branch) => (
                      <tr key={branch.id} className="rpt-row">
                        <td style={{ ...tbl.td, color: C.textLight, fontWeight: 600 }}>{branch.id}</td>
                        <td style={tbl.td}>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <img
                              alt={branch?.name}
                              src={
                                branch?.image
                                  ? process.env.REACT_APP_URL + branch.image
                                  : "https://cdn3.iconfinder.com/data/icons/linecons-free-vector-icons-pack/32/camera-512.png"
                              }
                              onError={(e) => {
                                e.target.src =
                                  "https://cdn3.iconfinder.com/data/icons/linecons-free-vector-icons-pack/32/camera-512.png";
                              }}
                              style={{
                                width: 44,
                                height: 44,
                                borderRadius: 10,
                                objectFit: "cover",
                                border: `1px solid ${C.borderLight}`,
                              }}
                            />
                            <div>
                              <div
                                style={{
                                  fontWeight: 700,
                                  color: C.text,
                                  maxWidth: 220,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={branch?.name}
                              >
                                {branch?.name || "—"}
                              </div>
                              {(() => {
                                const diff = merchantNameDiff(branch);
                                if (!diff.name && !diff.arabic) return null;
                                const text = [diff.name, diff.arabic].filter(Boolean).join(" · ");
                                return (
                                  <div
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: C.orange,
                                      maxWidth: 220,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                    title={`Name in the merchant system: ${text}`}
                                  >
                                    Merchant name: {text}
                                  </div>
                                );
                              })()}
                              {branch?.address && (
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: C.textLight,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    maxWidth: 220,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                  title={branch.address}
                                >
                                  <MapPin size={10} /> {branch.address}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td style={{ ...tbl.td, color: C.text, fontWeight: 600 }}>
                          {branch.merchant_branch_id || "—"}
                        </td>
                        {hasBrandRef && (
                          <td style={{ ...tbl.td, color: C.textMed, fontFamily: "monospace" }}>
                            {branch.brandReference || "—"}
                          </td>
                        )}
                        <td style={tbl.td}>
                          <FulfillmentPills fulfillment={branch.fulfillment} />
                        </td>
                        {hasBrands && (
                          <td style={tbl.td}>
                            <BrandChips brands={branch.brands} />
                          </td>
                        )}
                        <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>
                          {branch.is24Hours ? (
                            <TagBadge label="24 HOURS" color={C.blue} bg={C.blueLight} />
                          ) : (
                            `${branch.opening_time || "—"} – ${branch.closing_time || "—"}`
                          )}
                        </td>
                        <td style={tbl.td}>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            <StatusPill active={branch.active} />
                            {branch.snooze_online_orders && (
                              <TagBadge label="SNOOZED" color={C.orange} bg={C.orangeLight} />
                            )}
                            <PinWarning status={pinOf.get(branch.id)} />
                          </div>
                        </td>
                        <td style={tbl.td}>
                          <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
                            <ActionIcon
                              icon={Edit}
                              color={C.green}
                              title="Edit"
                              onClick={() => handleEdit(branch)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </RCard>
        </div>
      </Container>
    </Fragment>
  );
};

export default BranchesList;
